import { addDays } from 'date-fns';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { dateToUTCDate } from '@/features/accommodation/utils/date-to-utc-date';

import type { BasicFilters, BookingType, City, Dates, Guests, Region } from '../types';

type BookingFilterState = {
  selectedType: BookingType;
  filters: Record<BookingType, BasicFilters>;
};

type BookingFilterActions = {
  setSelectedType: (value: BookingType) => void;
  setRegion: (value: Region) => void;
  setCity: (value: City) => void;
  setDates: (value: Dates) => void;
  setGuests: (value: Guests) => void;
};

const now = dateToUTCDate(new Date());
const nowPlusOneDay = addDays(now, 1);

const yourTourInitialFilters: BasicFilters = {
  region: {
    id: '',
    name: '',
  },
  city: {
    id: '',
    name: '',
    timeZone: '',
  },
  dates: {
    from: now,
    to: nowPlusOneDay,
  },
  guests: {
    adults: 2,
    childrenAges: [],
  },
};
const accommodationInitialFilters: BasicFilters = {
  region: {
    id: '',
    name: '',
  },
  city: {
    id: '',
    name: '',
    timeZone: '',
  },
  dates: {
    from: now,
    to: nowPlusOneDay,
  },
  guests: {
    adults: 2,
    childrenAges: [],
  },
};
const excursionInitialFilters: BasicFilters = {
  region: {
    id: '',
    name: '',
  },
  city: {
    id: '',
    name: '',
    timeZone: '',
  },
  dates: {
    from: now,
    to: nowPlusOneDay,
  },
  guests: {
    adults: 2,
    childrenAges: [],
  },
};

export const useBookingFilterStore = create<BookingFilterState & BookingFilterActions>()(
  devtools(
    persist(
      (set) => ({
        selectedType: 'your-tour',
        filters: {
          'your-tour': yourTourInitialFilters,
          accommodation: accommodationInitialFilters,
          excursion: excursionInitialFilters,
        },
        setSelectedType: (value) => {
          set((state) => ({ selectedType: value, filters: state.filters }));
        },
        setRegion: (value) => {
          set((state) => ({
            selectedType: state.selectedType,
            filters: {
              ...state.filters,
              [state.selectedType]: {
                ...state.filters[state.selectedType],
                region: value,
              },
            },
          }));
        },
        setCity: (value) => {
          set((state) => ({
            selectedType: state.selectedType,
            filters: {
              ...state.filters,
              [state.selectedType]: { ...state.filters[state.selectedType], city: value },
            },
          }));
        },
        setDates: (value) => {
          set((state) => ({
            selectedType: state.selectedType,
            filters: {
              ...state.filters,
              [state.selectedType]: {
                ...state.filters[state.selectedType],
                dates: value,
              },
            },
          }));
        },
        setGuests: (value) => {
          set((state) => ({
            selectedType: state.selectedType,
            filters: {
              ...state.filters,
              [state.selectedType]: {
                ...state.filters[state.selectedType],
                guests: value,
              },
            },
          }));
        },
      }),
      {
        name: 'booking-filter-store',
        storage: createJSONStorage(() => sessionStorage, {
          reviver: (key, value) => {
            if (key === 'from' || key === 'to') {
              return new Date(value as string);
            }
            return value;
          },
        }),
      },
    ),
    { enabled: import.meta.env.MODE !== 'prod' },
  ),
);
